import { computed, nextTick, onMounted, reactive, Ref, watch } from 'vue';
import type { ICanvasPageHandle } from '@/components/v2/layout/canvas-page';
import { useConnectivity } from '@/composition/use-connectivity';
import { useSettingsSectionSpy } from '@/composition/reservations/use-settings-section-spy';
import {
  ICatalogPageContext,
  provideCatalogPage,
} from './catalog-page-context';
import { createCatalogPageState } from './catalog-page-state';
import { useCatalogAccess } from './use-catalog-access';
import { useCatalogActions } from './use-catalog-actions';
import { useCatalogCollapse } from './use-catalog-collapse';
import { useCatalogCommands } from './use-catalog-commands';
import { useCatalogData } from './use-catalog-data';
import { useCatalogHighlight } from './use-catalog-highlight';
import { useCatalogLayout } from './use-catalog-layout';
import { useCatalogLeaveGuard } from './use-catalog-leave-guard';
import { useCatalogSchedule } from './use-catalog-schedule';
import { useCatalogUrl } from './use-catalog-url';
import { useCatalogWindowing } from './use-catalog-windowing';

const SECTION_PREFIX = 'cat-';

export function catalogSectionId(categoryId: number): string {
  return `${SECTION_PREFIX}${categoryId}`;
}

/**
 * Builds and provides the Menú page context (plan §5.5): UI state, layout,
 * the derived catalog, access, URL sync, collapse, windowing, the
 * scroll-spy that feeds the rail and the assistant's row highlight.
 */
export function useCatalogPageSetup(
  canvas: Ref<ICanvasPageHandle | null>
): ICatalogPageContext {
  const state = reactive(createCatalogPageState());
  const layout = useCatalogLayout();
  const data = useCatalogData(state);
  const access = useCatalogAccess();
  const { status } = useConnectivity();
  const online = computed(() => status.value !== 'offline');
  const scroller = computed(() => canvas.value?.scroller ?? null);
  const loaded = computed(() => data.loadStatus.value === 'ready');

  /** A `?cId=` that arrived before the catalog: jumped to once it loads. */
  let pendingJump: number | null = null;

  const collapse = useCatalogCollapse({
    restaurantId: computed(() => data.restaurant.value?.id ?? null),
    menuId: () => state.menuId,
    sections: data.sections,
    productCount: computed(() => data.rows.value.length),
    isFiltering: data.isFiltering,
  });

  const windowing = useCatalogWindowing({
    sections: data.shownSections,
    isCollapsed: collapse.isCollapsed,
    rowMode: layout.rowMode,
    viewHeight: layout.viewHeight,
    scroller,
  });

  const spy = useSettingsSectionSpy({
    container: scroller,
    sections: computed(() =>
      data.shownSections.value.map((section) =>
        catalogSectionId(section.category.id)
      )
    ),
    elementFor: (section) => document.getElementById(section),
    topOffset: () => layout.headHeight.value,
    ready: computed(
      () => loaded.value && state.mode !== 'reorder' && !!scroller.value
    ),
    reconnectOn: () => layout.headHeight.value,
  });

  watch(
    spy.active,
    (section) => {
      state.activeCategoryId =
        section && section.startsWith(SECTION_PREFIX)
          ? Number(section.slice(SECTION_PREFIX.length))
          : null;
    },
    { immediate: true }
  );

  async function jumpToCategory(categoryId: number) {
    if (!loaded.value) {
      pendingJump = categoryId;
      return;
    }
    if (!data.categoryById.value.has(categoryId)) return;
    if (collapse.isCollapsed(categoryId))
      collapse.setCollapsed(categoryId, false);
    windowing.mount(categoryId);
    spy.go(catalogSectionId(categoryId));
    await nextTick();
    const element = document.getElementById(catalogSectionId(categoryId));
    if (!element || !canvas.value) return;
    canvas.value.scrollToElement(element);
    // Placeholders above it may have mounted meanwhile: correct once.
    window.requestAnimationFrame(() => {
      const again = document.getElementById(catalogSectionId(categoryId));
      if (again && canvas.value) canvas.value.scrollToElement(again);
    });
  }

  function nextFrame(): Promise<void> {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });
  }

  /**
   * Scrolls a product row to the middle of the page area below the sticky
   * head, unless it is fully in view already.
   */
  function scrollRowIntoView(itemId: number) {
    const page = canvas.value;
    const root = page?.scroller;
    const row = root?.querySelector<HTMLElement>(
      `.v2-catalog-product-row[data-item-id="${itemId}"]`
    );
    if (!page || !root || !row) return;
    const view = root.getBoundingClientRect();
    const box = row.getBoundingClientRect();
    const top = view.top + layout.headHeight.value;
    if (box.top >= top && box.bottom <= view.bottom) return;
    page.scrollToElement(
      row,
      Math.max(8, (view.bottom - top - box.height) / 2)
    );
  }

  /**
   * Brings a row of a category `jumpToCategory` just scrolled to on screen
   * (a long category can hold it far below its head): after that jump's own
   * correction frame, and once more a frame later for cards that mounted
   * meanwhile.
   */
  async function revealRow(itemId: number) {
    await nextFrame();
    scrollRowIntoView(itemId);
    await nextFrame();
    scrollRowIntoView(itemId);
  }

  const url = useCatalogUrl(state, {
    menuIds: computed(() => data.menus.value.map((menu) => menu.id)),
    menusLoaded: data.menusLoaded,
    debouncedQuery: data.debouncedQuery,
    onCategoryJump: (categoryId) => void jumpToCategory(categoryId),
  });

  // The first window once the catalog and the page height are known, and a
  // fresh one on every menu switch, filter or search change.
  watch(
    [loaded, () => layout.viewHeight.value > 0],
    ([isLoaded, hasHeight]) => {
      if (!isLoaded || !hasHeight) return;
      windowing.reset();
      if (pendingJump !== null) {
        const target = pendingJump;
        pendingJump = null;
        void jumpToCategory(target);
      }
    },
    { immediate: true }
  );
  // Another menu, filter or search: a new list from its top. Rows kept by
  // earlier actions (pins) belong to the previous one.
  watch([() => state.menuId, () => state.filter, data.debouncedQuery], () => {
    state.pinnedIds = {};
    windowing.reset();
    // Once the new list is rendered: scrolling before it would lay the old
    // list out again for nothing.
    void nextTick(() => {
      if (scroller.value) scroller.value.scrollTop = 0;
    });
  });

  // Another store (the page is reused): nothing of the previous one survives.
  watch(
    () => data.restaurant.value?.id,
    () => {
      state.selectedIds = {};
      state.pinnedIds = {};
      state.mode = 'browse';
      state.reorderCategoryId = null;
      state.reorderDirty = false;
      state.panel = null;
      pendingJump = null;
    }
  );

  useCatalogHighlight({
    state,
    data,
    url,
    loaded,
    jumpToCategory,
    revealRow,
  });

  const schedule = useCatalogSchedule(data);
  const { confirmDiscard, confirmReorderDiscard } =
    useCatalogLeaveGuard(state);
  const actions = useCatalogActions({
    state,
    data,
    access,
    online,
    url,
    editorDocked: () => layout.columns.value.asideDocked,
  });
  const commands = useCatalogCommands({
    state,
    data,
    url,
    actions,
    jumpToCategory,
    editorDocked: () => layout.columns.value.asideDocked,
    confirmReorderDiscard,
    confirmEditorDiscard: confirmDiscard,
  });

  onMounted(() => {
    // An HTTP load whatever the socket state; failures show the error state.
    data.ensureCatalogLoaded().catch(() => undefined);
  });

  const context: ICatalogPageContext = {
    state,
    layout,
    data,
    access,
    online,
    url,
    collapse,
    windowing,
    schedule,
    actions,
    commands,
    confirmDiscard,
    confirmReorderDiscard,
    jumpToCategory,
  };
  provideCatalogPage(context);
  return context;
}
